import { Badge, IconButton, Tooltip, Typography } from "@mui/material";
import { TemplateConfig } from "@types";
import debounce from "lodash/debounce";
import { Eye, EyeClosed } from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ContextChip } from "src/components/Form/TitleWithContext/ContextChip";
import { ContextState } from "src/components/Form/utils";
import { useDocEngine } from "src/hooks/useDocEngine";
import { RoadmapTasks } from "src/stores/models";

type Props = {
  contextConfig: ContextState;
  title: string;
  deviceId: string;
  templatesConfig: TemplateConfig;
  tasks: RoadmapTasks;
};

export const TitleWithContext = memo(
  ({
    contextConfig,
    title,
    deviceId,
    templatesConfig,
  }: Props) => {
    const docEngine = useDocEngine(deviceId);
    const [showContext, setShowContext] = useState(false);
    const [gradients, setGradients] = useState({ left: false, right: false });
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const dragStateRef = useRef({
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
    });

    const updateGradients = useCallback(() => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          scrollContainerRef.current;
        const isScrollable = scrollWidth > clientWidth;
        const isAtRightEdge =
          Math.abs(scrollWidth - clientWidth - scrollLeft) < 1;

        setGradients({
          left: scrollLeft > 0,
          right: isScrollable && !isAtRightEdge,
        });
      }
    }, []);

    // Debounced scroll handler
    const debouncedUpdateGradients = useMemo(
      () => debounce(updateGradients, 16), // ~60fps
      [updateGradients]
    );

    useEffect(() => {
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener("scroll", debouncedUpdateGradients);
        return () => {
          container.removeEventListener("scroll", debouncedUpdateGradients);
          debouncedUpdateGradients.cancel();
        };
      }
    }, [debouncedUpdateGradients]);

    useLayoutEffect(() => {
      if (showContext) {
        updateGradients();
      } else {
        setGradients({ left: false, right: false });
      }
    }, [showContext, contextConfig, updateGradients]);

    const navigateToIncompleteDocument = async (templateKey: string) => {
      await docEngine.navigateToWizardOrQMS({
        templateKey,
      });
    };

    const handleToggleContext = useCallback(() => {
      setShowContext((prev) => !prev);
    }, []);

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
      if (!scrollContainerRef.current) return;

      // Check if the click is on a ContextChip or its tooltip
      const target = e.target as HTMLElement;
      const isOnChip =
        target.closest('[role="button"]') ||
        target.closest(".MuiTooltip-popper") ||
        target.closest('[data-context-chip="true"]');

      if (isOnChip) {
        return; // Don't handle pointer events on chips/tooltips
      }

      const container = scrollContainerRef.current;

      dragStateRef.current = {
        isDragging: true,
        startX: e.pageX - container.offsetLeft,
        scrollLeft: container.scrollLeft,
      };

      container.setPointerCapture(e.pointerId);
    }, []);

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
      if (!dragStateRef.current.isDragging || !scrollContainerRef.current)
        return;

      const { startX, scrollLeft } = dragStateRef.current;
      const x = e.pageX - scrollContainerRef.current.offsetLeft;
      const walk = (x - startX) * 2;

      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    }, []);

    const handlePointerUp = useCallback((e: React.PointerEvent) => {
      if (!scrollContainerRef.current) return;

      dragStateRef.current.isDragging = false;
      scrollContainerRef.current.releasePointerCapture(e.pointerId);
    }, []);

    return (
      <div className="flex items-center gap-1 flex-1 min-w-0 min-h-[34px]">
        <Typography variant="body1" className="font-bold inline-flex">
          {title}
        </Typography>
        {contextConfig && contextConfig.size > 0 && (
          <>
            <Tooltip title={showContext ? "Hide context" : "Show context"}>
              <IconButton
                size="small"
                onClick={handleToggleContext}
                className="text-gray-600 z-10"
              >
                <Badge
                  color="error"
                  variant="dot"
                  invisible={Array.from(contextConfig.values())[0].complete}
                >
                  {showContext ? <EyeClosed size={16} /> : <Eye size={16} />}
                </Badge>
              </IconButton>
            </Tooltip>
            <div className="relative min-w-0 flex-1 overflow-hidden">
              {gradients.left && (
                <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10" />
              )}
              {gradients.right && (
                <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10" />
              )}
              <div
                className="flex items-center gap-1 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing touch-none"
                ref={scrollContainerRef}
                style={{
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  maxWidth: "100%",
                  userSelect: "none",
                  touchAction: "none",
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                {showContext &&
                  Array.from(contextConfig.entries()).map(([key, value]) => (
                    <ContextChip
                      key={key}
                      dataKey={key}
                      value={value}
                      handleDocumentClick={navigateToIncompleteDocument}
                      templatesConfig={templatesConfig}
                    />
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);
